import React, { useState, useEffect } from 'react';
import {
  Card,
  Button,
  Modal,
  Form,
  Input,
  Select,
  List,
  Tag,
  Space,
  Avatar,
  Typography,
  Divider,
  Empty,
  Badge,
  message,
  Row,
  Col
} from 'antd';
import {
  SendOutlined,
  MessageOutlined,
  UserOutlined,
  ShopOutlined,
  ClockCircleOutlined
} from '@ant-design/icons';
import { stationMessageAPI, stationOwnerAPI } from '../services/api';
import moment from 'moment';
import 'moment/locale/tr';

moment.locale('tr');

const { TextArea } = Input;
const { Option } = Select;
const { Title, Text } = Typography;

interface StationMessage {
  _id: string;
  fromStation: {
    _id: string;
    name: string;
    address: string;
  };
  toStation?: {
    _id: string;
    name: string;
    address: string;
  } | null;
  fromUser: {
    _id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
  toUser?: {
    _id: string;
    firstName: string;
    lastName: string;
    email: string;
  } | null;
  subject: string;
  message: string;
  messageType: 'info' | 'alert' | 'request' | 'announcement' | 'question';
  priority: 'low' | 'normal' | 'high' | 'urgent';
  status: 'sent' | 'delivered' | 'read' | 'archived';
  isRead: boolean;
  readAt?: string;
  replies: Array<{
    fromUser: {
      _id: string;
      firstName: string;
      lastName: string;
    };
    message: string;
    createdAt: string;
  }>;
  createdAt: string;
}

const StationMessages: React.FC = () => {
  const [messages, setMessages] = useState<StationMessage[]>([]);
  const [stations, setStations] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [sendModalVisible, setSendModalVisible] = useState(false);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState<StationMessage | null>(null);
  const [replyForm] = Form.useForm();
  const [sendForm] = Form.useForm();

  useEffect(() => {
    loadData();
    // Her 30 saniyede bir mesajları yenile
    const interval = setInterval(loadData, 30000);
    return () => clearInterval(interval);
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [messagesData, stationsData] = await Promise.all([
        stationMessageAPI.getMessages(),
        stationOwnerAPI.getStations()
      ]);
      setMessages(messagesData);
      setStations(stationsData);
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Mesajlar yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const handleSendMessage = async (values: any) => {
    try {
      await stationMessageAPI.sendMessage(values);
      message.success('Mesaj başarıyla gönderildi');
      setSendModalVisible(false);
      sendForm.resetFields();
      loadData();
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Mesaj gönderilemedi');
    }
  };

  const handleReply = async (messageId: string, replyText: string) => {
    try {
      await stationMessageAPI.replyToMessage(messageId, replyText);
      message.success('Yanıt gönderildi');
      replyForm.resetFields();
      loadData();
      if (selectedMessage) {
        const updated = await stationMessageAPI.getMessage(messageId);
        setSelectedMessage(updated);
      }
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Yanıt gönderilemedi');
    }
  };

  const handleArchive = async (id: string) => {
    try {
      await stationMessageAPI.archiveMessage(id);
      message.success('Mesaj arşivlendi');
      loadData();
    } catch (error: any) {
      message.error(error.response?.data?.message || 'İşlem başarısız');
    }
  };

  const handleViewDetail = async (msg: StationMessage) => {
    try {
      const detail = await stationMessageAPI.getMessage(msg._id);
      setSelectedMessage(detail);
      setDetailModalVisible(true);
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Mesaj detayı yüklenemedi');
    }
  };

  const getMessageTypeColor = (type: string) => {
    const colors: any = {
      info: 'blue',
      alert: 'orange',
      request: 'purple',
      announcement: 'green',
      question: 'cyan'
    };
    return colors[type] || 'default';
  };

  const getMessageTypeName = (type: string) => {
    const names: any = {
      info: 'Bilgi',
      alert: 'Uyarı',
      request: 'İstek',
      announcement: 'Duyuru',
      question: 'Soru'
    };
    return names[type] || type;
  };

  const getPriorityColor = (priority: string) => {
    const colors: any = {
      low: 'default',
      normal: 'blue',
      high: 'orange',
      urgent: 'red'
    };
    return colors[priority] || 'default';
  };

  const getPriorityName = (priority: string) => {
    const names: any = {
      low: 'Düşük',
      normal: 'Normal',
      high: 'Yüksek',
      urgent: 'Acil'
    };
    return names[priority] || priority;
  };

  const unreadCount = messages.filter(m => !m.isRead).length;

  return (
    <div>
      <Card>
        <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
          <Col>
            <Title level={2} style={{ margin: 0 }}>
              <MessageOutlined /> İstasyonlar Arası İletişim
            </Title>
            <Text type="secondary">
              İstasyonlarınız arasında mesajlaşın ve iletişim kurun
            </Text>
          </Col>
          <Col>
            <Space>
              {unreadCount > 0 && (
                <Badge count={unreadCount} size="small">
                  <Button icon={<MessageOutlined />}>Okunmamış Mesajlar</Button>
                </Badge>
              )}
              <Button
                type="primary"
                icon={<SendOutlined />}
                onClick={() => setSendModalVisible(true)}
                size="large"
              >
                Yeni Mesaj Gönder
              </Button>
            </Space>
          </Col>
        </Row>

        {messages.length === 0 ? (
          <Empty
            description="Henüz mesajınız yok"
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          >
            <Button type="primary" onClick={() => setSendModalVisible(true)}>
              İlk Mesajınızı Gönderin
            </Button>
          </Empty>
        ) : (
          <List
            loading={loading}
          dataSource={messages}
          renderItem={(msg) => (
            <List.Item
              style={{
                cursor: 'pointer',
                backgroundColor: msg.isRead ? '#fff' : '#f0f7ff',
                padding: '16px',
                marginBottom: '8px',
                borderRadius: '8px',
                border: msg.isRead ? '1px solid #f0f0f0' : '1px solid #1890ff'
              }}
              onClick={() => handleViewDetail(msg)}
            >
              <List.Item.Meta
                avatar={
                  <Badge dot={!msg.isRead}>
                    <Avatar icon={<UserOutlined />} />
                  </Badge>
                }
                title={
                  <Space>
                    <Text strong>{msg.subject}</Text>
                    <Tag color={getMessageTypeColor(msg.messageType)}>
                      {getMessageTypeName(msg.messageType)}
                    </Tag>
                    <Tag color={getPriorityColor(msg.priority)}>
                      {getPriorityName(msg.priority)}
                    </Tag>
                  </Space>
                }
                description={
                  <Space direction="vertical" size="small" style={{ width: '100%' }}>
                    <Space>
                      <ShopOutlined />
                      <Text type="secondary">
                        {msg.fromStation.name}
                        {msg.toStation && ` → ${msg.toStation.name}`}
                        {!msg.toStation && ' → Tüm İstasyonlar'}
                      </Text>
                    </Space>
                    <Text ellipsis style={{ display: 'block', maxWidth: '600px' }}>
                      {msg.message}
                    </Text>
                    <Space>
                      <ClockCircleOutlined />
                      <Text type="secondary">
                        {moment(msg.createdAt).format('DD MMMM YYYY, HH:mm')}
                      </Text>
                      {msg.replies && msg.replies.length > 0 && (
                        <>
                          <Divider type="vertical" />
                          <Text type="secondary">
                            {msg.replies.length} yanıt
                          </Text>
                        </>
                      )}
                    </Space>
                  </Space>
                }
              />
            </List.Item>
          )}
          />
        )}
      </Card>

      {/* Mesaj Gönderme Modal */}
      <Modal
        title="Yeni Mesaj Gönder"
        open={sendModalVisible}
        onCancel={() => {
          setSendModalVisible(false);
          sendForm.resetFields();
        }}
        onOk={() => sendForm.submit()}
        width={700}
        okText="Gönder"
        cancelText="İptal"
      >
        <Form
          form={sendForm}
          layout="vertical"
          onFinish={handleSendMessage}
        >
          <Form.Item
            name="fromStation"
            label="Gönderen İstasyon"
            rules={[{ required: true, message: 'Gönderen istasyon seçiniz' }]}
          >
            <Select placeholder="İstasyon seçiniz">
              {stations.map(station => (
                <Option key={station._id} value={station._id}>
                  {station.name}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="toStation"
            label="Alıcı İstasyon"
            tooltip="Boş bırakılırsa tüm istasyonlara gönderilir"
          >
            <Select placeholder="Tüm İstasyonlar" allowClear>
              {stations.map(station => (
                <Option key={station._id} value={station._id}>
                  {station.name}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="subject"
            label="Konu"
            rules={[{ required: true, message: 'Konu gereklidir' }]}
          >
            <Input placeholder="Mesaj konusu" />
          </Form.Item>

          <Form.Item
            name="message"
            label="Mesaj"
            rules={[{ required: true, message: 'Mesaj içeriği gereklidir' }]}
          >
            <TextArea rows={6} placeholder="Mesajınızı yazın..." />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="messageType"
                label="Mesaj Tipi"
                initialValue="info"
              >
                <Select>
                  <Option value="info">Bilgi</Option>
                  <Option value="alert">Uyarı</Option>
                  <Option value="request">İstek</Option>
                  <Option value="announcement">Duyuru</Option>
                  <Option value="question">Soru</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="priority"
                label="Öncelik"
                initialValue="normal"
              >
                <Select>
                  <Option value="low">Düşük</Option>
                  <Option value="normal">Normal</Option>
                  <Option value="high">Yüksek</Option>
                  <Option value="urgent">Acil</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>

      {/* Mesaj Detay Modal */}
      <Modal
        title={
          <Space>
            <MessageOutlined />
            <span>{selectedMessage?.subject}</span>
            <Tag color={getMessageTypeColor(selectedMessage?.messageType || 'info')}>
              {getMessageTypeName(selectedMessage?.messageType || 'info')}
            </Tag>
            <Tag color={getPriorityColor(selectedMessage?.priority || 'normal')}>
              {getPriorityName(selectedMessage?.priority || 'normal')}
            </Tag>
          </Space>
        }
        open={detailModalVisible}
        onCancel={() => {
          setDetailModalVisible(false);
          setSelectedMessage(null);
          replyForm.resetFields();
        }}
        footer={null}
        width={800}
      >
        {selectedMessage && (
          <div>
            <Space direction="vertical" style={{ width: '100%' }} size="large">
              <div>
                <Space>
                  <ShopOutlined />
                  <Text strong>Gönderen:</Text>
                  <Text>{selectedMessage.fromStation.name}</Text>
                </Space>
                {selectedMessage.toStation && (
                  <>
                    <Divider type="vertical" />
                    <Text strong>Alıcı:</Text>
                    <Text>{selectedMessage.toStation.name}</Text>
                  </>
                )}
                {!selectedMessage.toStation && (
                  <>
                    <Divider type="vertical" />
                    <Text strong>Alıcı:</Text>
                    <Text>Tüm İstasyonlar</Text>
                  </>
                )}
              </div>

              <div>
                <Text strong>Gönderen:</Text>
                <Text> {selectedMessage.fromUser.firstName} {selectedMessage.fromUser.lastName}</Text>
                <Divider type="vertical" />
                <Text type="secondary">
                  {moment(selectedMessage.createdAt).format('DD MMMM YYYY, HH:mm')}
                </Text>
              </div>

              <Divider />

              <div>
                <Text>{selectedMessage.message}</Text>
              </div>

              {selectedMessage.replies && selectedMessage.replies.length > 0 && (
                <>
                  <Divider>Yanıtlar ({selectedMessage.replies.length})</Divider>
                  <List
                    dataSource={selectedMessage.replies}
                    renderItem={(reply) => (
                      <List.Item>
                        <List.Item.Meta
                          avatar={<Avatar icon={<UserOutlined />} />}
                          title={
                            <Space>
                              <Text strong>
                                {reply.fromUser.firstName} {reply.fromUser.lastName}
                              </Text>
                              <Text type="secondary">
                                {moment(reply.createdAt).format('DD MMMM YYYY, HH:mm')}
                              </Text>
                            </Space>
                          }
                          description={reply.message}
                        />
                      </List.Item>
                    )}
                  />
                </>
              )}

              <Divider />

              <Form
                form={replyForm}
                onFinish={(values) => handleReply(selectedMessage._id, values.reply)}
              >
                <Form.Item
                  name="reply"
                  rules={[{ required: true, message: 'Yanıt mesajı gereklidir' }]}
                >
                  <TextArea rows={4} placeholder="Yanıtınızı yazın..." />
                </Form.Item>
                <Form.Item>
                  <Space>
                    <Button type="primary" htmlType="submit" icon={<SendOutlined />}>
                      Yanıtla
                    </Button>
                    <Button onClick={() => handleArchive(selectedMessage._id)}>
                      Arşivle
                    </Button>
                  </Space>
                </Form.Item>
              </Form>
            </Space>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default StationMessages;

